import {FADE_MAX, FADE_MIN, IMAGE_MAX_SECONDS, MIN_LEN} from './constants.js'
import {in_transition, out_transition} from './utils.js'
import {resolve_overlaps} from './layout.js'
import {resolve_tracks} from './tracks.js'

function export_transition (tr, len) {
	const fade = tr.type == "fade" && tr.duration > 0;
	return {type: fade ? "fade" : "cut", duration: fade ? Math.min(tr.duration, len) : 0};
}

function import_transition (tr) {
	const duration = Number(_.get(tr, "duration"));
	return {type: _.get(tr, "type") == "fade" ? "fade" : "cut", duration: duration > 0 ? _.clamp(duration, FADE_MIN, FADE_MAX) : 1};
}

// Serializes the timeline; `layout` holds the playback positions of the video clips
export function build_edl ({title, duration, tracks, layout, overlays, audios}) {
	return {
		version: 1,
		title: title,
		duration: duration,
		// Higher z_index is drawn in front; the track list itself is ordered from front to back
		tracks: _.map(tracks, (t, i) => ({id: t.id, kind: t.kind, name: t.name, z_index: tracks.length - i, hidden: t.hidden, muted: !!t.muted})),
		clips: _.map(layout, (l) => {
			return {
				id: l.clip.id,
				order: l.order,
				track: l.track,
				x: l.clip.x,
				y: l.clip.y,
				width: l.clip.w,
				aspect: l.clip.aspect,
				opacity: l.clip.opacity,
				volume: l.clip.volume,
				muted: !!l.clip.muted,
				type: l.clip.type || "video",
				title: l.clip.title,
				source: l.clip.source,
				url: l.clip.url,
				poster_url: l.clip.poster_url,
				qualities: l.clip.qualities,
				in: l.clip.in,
				out: l.clip.out,
				source_duration: l.clip.source_duration,
				duration: l.len,
				timeline_start: l.start,
				// A fade between touching clips is a cross-fade over the overlap of the two clips; otherwise the clip fades in from its start
				transition: {
					type: l.clip.transition.type,
					duration: l.xfade ? l.xfade.length : l.clip.transition.type == "fade" ? Math.min(l.clip.transition.duration, l.len) : 0,
					crossfade: !!l.xfade,
					previous_clip: l.xfade ? l.xfade.prev_id : undefined
				},
				transition_out: {
					type: l.fade_out > 0 ? "fade" : "cut",
					duration: l.fade_out
				}
			};
		}),
		overlays: _.map(_.sortBy(overlays, "start"), (o) => ({
			id: o.id,
			track: o.track,
			title: o.title,
			source: o.source,
			url: o.url,
			start: o.start,
			duration: o.duration,
			x: o.x,
			y: o.y,
			width: o.w,
			aspect: o.aspect,
			opacity: o.opacity,
			transition: export_transition(in_transition(o), o.duration),
			transition_out: export_transition(out_transition(o), o.duration)
		})),
		audios: _.map(_.sortBy(audios, "start"), (a) => ({
			id: a.id,
			track: a.track,
			title: a.title,
			source: a.source,
			url: a.url,
			start: a.start,
			in: a.in,
			out: a.in + a.duration,
			duration: a.duration,
			source_duration: a.source_duration,
			volume: a.volume
		}))
	};
}

// Validates an exported timeline and turns it into editor state; throws on invalid input
export function parse_edl (edl, next_id) {
	if (!edl || !Array.isArray(edl.clips)) throw new Error("Invalid timeline file");
	const frame = (item, label) => {
		const x = Number(item.x);
		const y = Number(item.y);
		const w = Number(item.width);
		const aspect = Number(item.aspect);
		const opacity = item.opacity == null ? 100 : Number(item.opacity);
		if (![x, y, w, aspect, opacity].every(isFinite) || w <= 0 || aspect <= 0) throw new Error("Invalid position in " + label);
		return {x: x, y: y, w: _.clamp(w, 5, 200), aspect: aspect, opacity: _.clamp(Math.round(opacity), 0, 100), placed: true};
	};
	const clips = _.map(edl.clips, (c, i) => {
		const cin = Number(c.in);
		const cout = Number(c.out);
		if (typeof c.url != "string" || !c.url || !isFinite(cin) || !isFinite(cout) || cin < 0 || cout - cin < MIN_LEN) {
			throw new Error("Invalid clip #" + (i + 1));
		}
		const start = Number(c.timeline_start);
		const volume = Number(c.volume);
		if (!isFinite(start) || start < 0 || !isFinite(volume)) throw new Error("Invalid clip #" + (i + 1));
		return {
			id: next_id(),
			type: c.type == "image" ? "image" : "video",
			track: c.track,
			start: start,
			...frame(c, "clip #" + (i + 1)),
			volume: _.clamp(Math.round(volume), 0, 100),
			muted: c.muted === true,
			title: String(c.title || "Clip"),
			url: c.url,
			poster_url: typeof c.poster_url == "string" && c.poster_url ? c.poster_url : undefined,
			qualities: _.map(_.filter(c.qualities, (q) => q && typeof q.url == "string" && q.url && q.uuid), (q) => _.pick(q, ["uuid", "url", "name", "title", "subtitle"])),
			source: c.source,
			source_duration: c.type == "image" ? IMAGE_MAX_SECONDS : Math.max(Number(c.source_duration) || 0, cout),
			in: cin,
			out: cout,
			transition: import_transition(c.transition),
			transition_out: import_transition(c.transition_out)
		};
	});
	const overlays = _.map(edl.overlays || [], (o, i) => {
		const start = Number(o.start);
		const duration = Number(o.duration);
		if (typeof o.url != "string" || !o.url || !isFinite(start) || start < 0 || !isFinite(duration) || duration < MIN_LEN) {
			throw new Error("Invalid overlay #" + (i + 1));
		}
		return {
			id: next_id(),
			title: String(o.title || "Image"),
			url: o.url,
			source: o.source,
			start: start,
			duration: duration,
			track: o.track,
			...frame(o, "overlay #" + (i + 1)),
			transition: import_transition(o.transition),
			transition_out: import_transition(o.transition_out)
		};
	});
	const audios = _.map(edl.audios || [], (a, i) => {
		const start = Number(a.start);
		const offset = Number(a.in) || 0;
		const duration = Number(a.duration);
		if (typeof a.url != "string" || !a.url || !isFinite(start) || start < 0 || offset < 0 || !isFinite(duration) || duration < MIN_LEN || !isFinite(Number(a.volume))) {
			throw new Error("Invalid audio #" + (i + 1));
		}
		return {
			id: next_id(),
			title: String(a.title || "Audio"),
			url: a.url,
			source: a.source,
			start: start,
			in: offset,
			duration: duration,
			track: a.track,
			source_duration: Math.max(Number(a.source_duration) || 0, offset + duration),
			volume: _.clamp(Math.round(Number(a.volume)), 0, 100)
		};
	});
	const tracks = resolve_tracks(edl, {video: clips, overlay: overlays, audio: audios});
	resolve_overlaps(clips);
	return {title: String(edl.title || ""), tracks, clips, overlays, audios};
}
